import React, { useState } from 'react';
import { Button } from '../../design-system/Button';
import { Input } from '../../design-system/Input';
import { coachOnboardingAPI } from '../../../api/coachApi';
import { BusinessSetupFormData, BUSINESS_TYPE_DISPLAY, TIMEZONE_FLEXIBILITY_DISPLAY } from '../../../types/coach';

interface BusinessSetupStepProps {
  onComplete: () => void;
  onSkip?: () => void;
  progress?: any;
}

const BusinessSetupStep: React.FC<BusinessSetupStepProps> = ({ onComplete }) => {
  const [formData, setFormData] = useState<BusinessSetupFormData>({
    business_type: 'INDIVIDUAL',
    business_name: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    accepts_international_clients: true,
    timezone_flexibility: 'MODERATE',
    max_clients: 20,
    client_screening_required: false,
    client_screening_questions: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError('');
      await coachOnboardingAPI.updateBusinessSetup(formData);
      onComplete();
    } catch (err: any) {
      setError(err.message || 'Failed to update information');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Business Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Business Type *
        </label>
        <select
          value={formData.business_type}
          onChange={(e) => setFormData({ ...formData, business_type: e.target.value as any })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B5FDC]"
        >
          {Object.entries(BUSINESS_TYPE_DISPLAY).map(([key, display]) => (
            <option key={key} value={key}>{display}</option>
          ))}
        </select>
      </div>

      {/* Business Name */}
      <div>
        <label htmlFor="business-name" className="block text-sm font-medium text-gray-700 mb-2">
          Business Name (Optional)
        </label>
        <Input
          id="business-name"
          type="text"
          placeholder="Your coaching business name"
          value={formData.business_name}
          onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
        />
      </div>

      {/* Timezone */}
      <div>
        <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
          Primary Timezone *
        </label>
        <Input
          id="timezone"
          type="text"
          value={formData.timezone}
          onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
          placeholder="e.g., America/New_York"
        />
        <p className="mt-1 text-sm text-gray-500">
          Your current timezone: {formData.timezone}
        </p>
      </div>

      {/* International Clients */}
      <div>
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={formData.accepts_international_clients}
            onChange={(e) => setFormData({ ...formData, accepts_international_clients: e.target.checked })}
            className="h-4 w-4 text-[#4B5FDC] focus:ring-[#4B5FDC] border-gray-300 rounded"
          />
          <span className="text-sm font-medium text-gray-700">
            Accept international clients
          </span>
        </label>
      </div>

      {/* Timezone Flexibility */}
      {formData.accepts_international_clients && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone Flexibility
          </label>
          <select
            value={formData.timezone_flexibility}
            onChange={(e) => setFormData({ ...formData, timezone_flexibility: e.target.value as any })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B5FDC]"
          >
            {Object.entries(TIMEZONE_FLEXIBILITY_DISPLAY).map(([key, display]) => (
              <option key={key} value={key}>{display}</option>
            ))}
          </select>
        </div>
      )}

      {/* Max Clients */}
      <div>
        <label htmlFor="max-clients" className="block text-sm font-medium text-gray-700 mb-2">
          Maximum Concurrent Clients *
        </label>
        <Input
          id="max-clients"
          type="number"
          min="1"
          max="100"
          value={formData.max_clients}
          onChange={(e) => setFormData({ ...formData, max_clients: parseInt(e.target.value) || 20 })}
          className="w-32"
        />
        <p className="mt-1 text-sm text-gray-500">
          The maximum number of clients you can work with at one time
        </p>
      </div>

      {/* Client Screening */}
      <div>
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={formData.client_screening_required}
            onChange={(e) => setFormData({ ...formData, client_screening_required: e.target.checked })}
            className="h-4 w-4 text-[#4B5FDC] focus:ring-[#4B5FDC] border-gray-300 rounded"
          />
          <span className="text-sm font-medium text-gray-700">
            Require client screening before accepting
          </span>
        </label>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-6">
        <Button type="submit" isLoading={loading} size="lg">
          Continue
        </Button>
      </div>
    </form>
  );
};

export default BusinessSetupStep;